import React, { useState } from "react";
import {
  motion,
  useReducedMotion,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
} from "framer-motion";

const item = {
  hidden: { opacity: 0, y: 24, scale: 0.95, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const ringMask = {
  WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
  WebkitMaskComposite: "xor",
  mask: "linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)",
};

export default function CourseFrame({ children, className = "", tilt = true }) {
  const reduceMotion = useReducedMotion();
  const tiltEnabled = tilt && !reduceMotion;
  const [hovered, setHovered] = useState(false);

  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const sx = useSpring(px, { stiffness: 120, damping: 20 });
  const sy = useSpring(py, { stiffness: 120, damping: 20 });
  const rotateY = useTransform(sx, [0, 1], [-4, 4]);
  const rotateX = useTransform(sy, [0, 1], [4, -4]);

  const spotlight = useMotionTemplate`radial-gradient(240px circle at ${mx}px ${my}px, rgba(255,255,255,0.14), transparent 70%)`;
  const borderGlow = useMotionTemplate`radial-gradient(160px circle at ${mx}px ${my}px, rgba(255,255,255,0.95), transparent 70%)`;

  const handleMove = (e) => {
    if (reduceMotion) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    mx.set(x);
    my.set(y);
    px.set(x / r.width);
    py.set(y / r.height);
  };

  const handleLeave = () => {
    setHovered(false);
    px.set(0.5);
    py.set(0.5);
  };

  return (
    <motion.div
      variants={item}
      onMouseMove={handleMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleLeave}
      whileHover={reduceMotion ? undefined : { y: -6, scale: 1.015 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      style={tiltEnabled ? { rotateX, rotateY, transformPerspective: 1000 } : undefined}
      className={`group/course relative h-full rounded-[15px] ${className}`}
    >
      {/* soft glow behind the card */}
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-2 rounded-[22px] bg-white/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover/course:opacity-100"
      />

      {children}

      {!reduceMotion && (
        <>
          {/* cursor spotlight over the card */}
          <motion.span
            aria-hidden
            className="pointer-events-none absolute inset-0 z-10 rounded-[15px]"
            style={{ background: spotlight }}
            animate={{ opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />

          {/* border brightens around the cursor */}
          <motion.span
            aria-hidden
            className="pointer-events-none absolute inset-0 z-10 rounded-[15px] p-px"
            style={ringMask}
            animate={{ opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.span className="block h-full w-full" style={{ background: borderGlow }} />
          </motion.span>
        </>
      )}

      {/* corner brackets that appear on hover */}
      {["top-2 left-2 border-t border-l", "top-2 right-2 border-t border-r", "bottom-2 left-2 border-b border-l", "bottom-2 right-2 border-b border-r"].map(
        (pos) => (
          <span
            key={pos}
            aria-hidden
            className={`pointer-events-none absolute z-20 h-3 w-3 border-white/0 opacity-0 transition-all duration-300 group-hover/course:border-white/60 group-hover/course:opacity-100 ${pos}`}
          />
        )
      )}

      {/* light sweep on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-[15px]"
      >
        <span className="absolute inset-y-0 w-1/2 -translate-x-full -skew-x-[20deg] bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 ease-out group-hover/course:translate-x-[250%]" />
      </span>
    </motion.div>
  );
}